import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { useMPEFetch, useMutateMPEFetch } from '@retail/app/hooks';
import { buildUrl } from '@shared/utils';
import { mpeApi } from '@retail/mgpris/config';
import { QueryOptions } from '@shared/fetch-utils';
import {
  CreatePriceRoundingRuleConfigDTO,
  PriceRoundingRuleConfigDTO,
  PriceSimulationResult,
  SimulatePriceRoundingRuleConfigDTO,
} from '@retail/rounding/types';

interface FetchPriceRoundingRulesProps extends QueryOptions {
  assortmentId: number;
}

export function useFetchPriceRoundingRules({
  assortmentId,
  ...queryOptions
}: FetchPriceRoundingRulesProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<PriceRoundingRuleConfigDTO[]>({
    method: 'GET',
    queryKey: ['priceRounding', assortmentId],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortments.assortment.priceRoundingRules.ruleSets.getFullLinkPath({
        assortmentId,
      }),
    }),
    ...queryOptions,
  });
}

interface FetchPriceRoundingRuleByIdProps extends QueryOptions {
  ruleSetId: number | undefined;
}

export function useFetchPriceRoundingRuleById({
  ruleSetId,
  ...queryOptions
}: FetchPriceRoundingRuleByIdProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<PriceRoundingRuleConfigDTO>({
    method: 'GET',
    queryKey: ['priceRounding', ruleSetId],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.priceRounding.ruleSets.ruleSet.getFullLinkPath({
        ruleSetId: ruleSetId as number,
      }),
    }),
    enabled: ruleSetId !== undefined,
    ...queryOptions,
  });
}

interface CreatePriceRoundingRuleSetProps extends QueryOptions {
  assortmentId: number;
}

export function useCreatePriceRoundingRuleSet({
  assortmentId,
  ...queryOptions
}: CreatePriceRoundingRuleSetProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, CreatePriceRoundingRuleConfigDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortments.assortment.priceRoundingRules.ruleSets.getFullLinkPath({
        assortmentId,
      }),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}

export function useUpdatePriceRoundingRuleSet(queryOptions: QueryOptions = {}) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, CreatePriceRoundingRuleConfigDTO, { ruleSetId: number }>({
    url: ({ ruleSetId }) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.priceRounding.ruleSets.ruleSet.getFullLinkPath({
          ruleSetId,
        }),
      }),
    method: 'PUT',
    token,
    ...queryOptions,
  });
}

export function useDeletePriceRoundingRuleSet(queryOptions: QueryOptions = {}) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, void, { ruleSetId: number }>({
    url: ({ ruleSetId }) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.priceRounding.ruleSets.ruleSet.getFullLinkPath({
          ruleSetId,
        }),
      }),
    method: 'DELETE',
    token,
    ...queryOptions,
  });
}

export function useSimulatePriceRoundingRuleSet(queryOptions: QueryOptions = {}) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<PriceSimulationResult, SimulatePriceRoundingRuleConfigDTO>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.priceRounding.simulation.getFullLinkPath(),
    }),
    method: 'POST',
    token,
    ...queryOptions,
  });
}
